import { z } from "zod";
import { RuntimeProviderSchema } from "./budget.js";
import { getApprovedLlmModelEntry } from "./llmModelRegistry.js";
import { IsoTimestampSchema, NonEmptyStringSchema, PositiveIntegerSchema } from "./source.js";
/**
 * The Firestore doc id for the single platform-wide LLM provider settings
 * document (`system_settings/llm-provider`, see
 * `_build/LLM-PROVIDER-PLAN-2026-07-31.md`). The doc only ever *selects*
 * among pairs already approved in `llmModelRegistry.ts` — it may never
 * introduce a new provider/model pair.
 */
export const LLM_PROVIDER_SETTINGS_ID = "llm-provider";
export const LLM_PROVIDER_SETTINGS_SCHEMA_VERSION = "llm-provider-settings-v1";
/** The OpenAI Responses API effort vocabulary accepted by the settings doc. */
const LLM_REASONING_EFFORT_VALUES = [
    "none",
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
    "max"
];
export const LlmReasoningEffortSchema = z.enum(LLM_REASONING_EFFORT_VALUES);
/**
 * Read the supported effort ladder from the selected model's declared registry
 * policy. The returned order is the platform's ascending effort order, not an
 * adapter fallback or a provider-wide guess.
 */
export function getSupportedLlmReasoningEfforts(provider, modelId) {
    const policy = getApprovedLlmModelEntry(provider, modelId)?.reasoningEffort;
    if (!policy) {
        return [];
    }
    const declared = new Set([
        ...policy.bands.map((band) => band.effort),
        policy.aboveHighestBand,
        ...(policy.defaultEffort ? [policy.defaultEffort] : [])
    ]);
    return LLM_REASONING_EFFORT_VALUES.filter((effort) => declared.has(effort));
}
export function getRegistryDefaultLlmReasoningEffort(provider, modelId) {
    return getApprovedLlmModelEntry(provider, modelId)?.reasoningEffort?.defaultEffort;
}
export const LlmProviderSettingsSchema = z
    .object({
    id: z.literal(LLM_PROVIDER_SETTINGS_ID),
    schemaVersion: z.literal(LLM_PROVIDER_SETTINGS_SCHEMA_VERSION),
    activeProvider: RuntimeProviderSchema,
    activeModelId: NonEmptyStringSchema,
    activeReasoningEffort: LlmReasoningEffortSchema.optional(),
    revision: PositiveIntegerSchema,
    updatedAt: IsoTimestampSchema,
    updatedBy: z
        .object({
        uid: NonEmptyStringSchema,
        email: NonEmptyStringSchema
    })
        .strict(),
    note: NonEmptyStringSchema.optional()
})
    .strict();
//# sourceMappingURL=llmSettings.js.map